import { ButtonAppearance, Icon, IconSize, Spinner, SpinnerSize, Text } from '@bechtel/gds';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { AIIcon } from '../ai-assistant-widget/ai-icon';
import { AIAssistantContext, AIAssistantMessage } from '../ai-assistant-widget/types';
import {
  AIAssistantEmbeddedButtonStyled,
  AIAssistantEmbeddedCloseButtonStyled,
  AIAssistantEmbeddedContainerStyled,
  AIAssistantEmbeddedHeaderStyled,
  AIAssistantEmbeddedIframeContainerStyled,
  AIAssistantEmbeddedIframeStyled,
  AIAssistantEmbeddedLoadingStyled,
  AIAssistantEmbeddedPanelStyled,
} from './styled';

export interface AIAssistantEmbeddedProps {
  /**
   * URL of the AI Assistant application to load in iframe
   */
  assistantUrl: string;
  /**
   * Whether the panel is currently open
   */
  isOpen: boolean;
  /**
   * Callback to close the panel
   */
  onClose: () => void;
  /**
   * Optional callback when widget is expanded
   */
  onExpand?: () => void;
  /**
   * Optional external ref for the iframe (for context provider integration)
   */
  iframeRef?: React.RefObject<HTMLIFrameElement>;
}

/**
 * Embedded AI Assistant Panel - Opens from the right side of the screen
 * This version is controlled by parent component (no floating button)
 */
export const AIAssistantEmbeddedPanel: FC<AIAssistantEmbeddedProps> = ({
  assistantUrl,
  isOpen,
  onClose,
  onExpand,
  iframeRef: externalIframeRef,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const internalIframeRef = useRef<HTMLIFrameElement>(null);
  const iframeRef = externalIframeRef || internalIframeRef;
  const contextRef = useRef<AIAssistantContext | null>(null);

  // Handle iframe load
  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);

    // Send initial context when iframe loads
    if (contextRef.current && iframeRef.current?.contentWindow) {
      const message: AIAssistantMessage = {
        type: 'context-update',
        payload: contextRef.current,
      };
      iframeRef.current.contentWindow.postMessage(message, assistantUrl);
    }
  }, [assistantUrl]);

  // Method to update context
  const updateContext = useCallback(
    (context: AIAssistantContext) => {
      contextRef.current = context;

      if (iframeRef.current?.contentWindow && !isLoading) {
        const message: AIAssistantMessage = {
          type: 'context-update',
          payload: context,
        };
        iframeRef.current.contentWindow.postMessage(message, assistantUrl);
      }
    },
    [assistantUrl, isLoading]
  );

  // Expose updateContext method via ref
  useEffect(() => {
    (window as any).__aiAssistantUpdateContext = updateContext;
    return () => {
      delete (window as any).__aiAssistantUpdateContext;
    };
  }, [updateContext]);

  // Handle messages from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        const messageUrl = new URL(assistantUrl);
        const eventUrl = new URL(event.origin);

        if (messageUrl.origin !== eventUrl.origin) {
          return;
        }
      } catch (error) {
        console.warn('Invalid URL in postMessage origin check:', error);
        return;
      }

      const message: AIAssistantMessage = event.data;

      switch (message.type) {
        case 'ready':
          if (contextRef.current && iframeRef.current?.contentWindow) {
            const contextMessage: AIAssistantMessage = {
              type: 'context-update',
              payload: contextRef.current,
            };
            iframeRef.current.contentWindow.postMessage(contextMessage, assistantUrl);
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [assistantUrl]);

  // Keyboard shortcut to close (Escape)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Call onExpand when opened
  useEffect(() => {
    if (isOpen) {
      onExpand?.();
    }
  }, [isOpen, onExpand]);

  return (
    <>
      {/* Panel - no overlay so users can interact with background */}
      <AIAssistantEmbeddedContainerStyled>
        <AIAssistantEmbeddedPanelStyled isOpen={isOpen} data-testid="ai-assistant-embedded-panel">
          {/* Header */}
          <AIAssistantEmbeddedHeaderStyled>
            <Text size="500" weight="600" color="white" family="graphik">
              AI Assistant
            </Text>
            <AIAssistantEmbeddedCloseButtonStyled
              appearance={ButtonAppearance.TERTIARY}
              onClick={onClose}
              aria-label="Close AI Assistant"
              data-testid="ai-assistant-embedded-close"
            >
              <Icon icon="times" size={IconSize.MEDIUM} fillColor="white" />
            </AIAssistantEmbeddedCloseButtonStyled>
          </AIAssistantEmbeddedHeaderStyled>

          {/* Iframe Container */}
          <AIAssistantEmbeddedIframeContainerStyled>
            {isLoading && isOpen && (
              <AIAssistantEmbeddedLoadingStyled>
                <Spinner size={SpinnerSize.LARGE} />
              </AIAssistantEmbeddedLoadingStyled>
            )}
            <AIAssistantEmbeddedIframeStyled
              ref={iframeRef}
              src={assistantUrl}
              title="AI Assistant"
              onLoad={handleIframeLoad}
              allow="clipboard-read; clipboard-write"
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
              data-testid="ai-assistant-embedded-iframe"
              style={{ display: isOpen ? 'block' : 'none' }}
            />
          </AIAssistantEmbeddedIframeContainerStyled>
        </AIAssistantEmbeddedPanelStyled>
      </AIAssistantEmbeddedContainerStyled>
    </>
  );
};

/**
 * AI Assistant Button - Can be placed anywhere in the UI
 * Uses an icon button that can be styled to match the context
 */
export interface AIAssistantButtonProps {
  onClick: () => void;
  isCollapsed?: boolean;
  variant?: 'nav' | 'header' | 'fab';
}

export const AIAssistantButton: FC<AIAssistantButtonProps> = ({ onClick, isCollapsed = false, variant = 'nav' }) => {
  return (
    <AIAssistantEmbeddedButtonStyled
      onClick={onClick}
      isCollapsed={isCollapsed}
      variant={variant}
      data-testid="ai-assistant-button"
      aria-label="Open AI Assistant"
    >
      <AIIcon size={isCollapsed ? 24 : 20} color="white" />
      {!isCollapsed && variant === 'nav' && (
        <Text weight="600" size="400" color="white" family="graphik" style={{ marginLeft: '12px' }}>
          AI Assistant
        </Text>
      )}
    </AIAssistantEmbeddedButtonStyled>
  );
};
